import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Printer, Download, ArrowRight } from "lucide-react";

/**
 * The Field Manual — the actual lead magnet content.
 *
 * Designed to:
 *  - Read beautifully inline on any device (long-form web reading).
 *  - Print cleanly to a 7-page PDF via Cmd+P / browser print dialog.
 *  - Carry forward the same brand voice as the Field Notes.
 */
export default function FieldManual() {
  return (
    <Layout>
      <SEO
        title="The Forged in Fire Field Manual"
        description="A short, practical primer on how leaders are actually formed — by the moments they don't ask for, and the work they do the week before."
      />
      <article className="field-manual" data-testid="field-manual">
        {/* COVER */}
        <section className="page-cover bg-brand-ink text-white">
          <div className="container-narrow max-w-3xl py-24 md:py-32">
            <p className="overline text-brand-rust">A Forged in Fire field manual</p>
            <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1.05]">
              The leader you become<br />
              <em className="italic text-brand-rust">in the fire.</em>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed">
              A short, practical primer on how leaders are actually formed — by the moments they don&apos;t ask for, and the work they do in the week before those moments arrive.
            </p>
            <p className="mt-12 overline text-white/60">By Robbie South · South on Leadership</p>

            <div className="mt-16 flex flex-wrap gap-4 print:hidden">
              <button
                onClick={() => window.print()}
                data-testid="field-manual-print"
                className="inline-flex items-center gap-2 bg-white text-brand-ink hover:bg-brand-bg px-6 py-3 text-sm font-medium tracking-wide transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print / save as PDF
              </button>
              <a
                href="#start"
                className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-brand-ink px-6 py-3 text-sm font-medium tracking-wide transition-colors"
              >
                Start reading <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* INTRO */}
        <section id="start" className="container-narrow max-w-3xl py-16 md:py-24">
          <p className="overline text-brand-rust">Before we begin</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 text-brand-ink leading-tight">
            This is not a theory of leadership. It is a record of what stays standing after the fire.
          </h2>

          <div className="prose-essay mt-10">
            <p>I spent the better part of two decades watching leaders fail in two specific ways. The first was loud — a public collapse, a missed quarter, a fired team. The second was quiet, and much more common — a slow drift where the leader was technically still in the chair, but the standard had already left the room.</p>

            <p>The second kind is the one that scares me. I have been that leader. The drift is patient. It will wait you out.</p>

            <p>What follows is the shortest, most honest version I can give you of what I&apos;ve learned about being the kind of leader that holds up when the room gets hot. It is not comprehensive. It is not impressive. It is the small set of things I would tell my younger self, on a card, before sending him into his first hard week.</p>

            <p>Read it once. Then put it down. Then come back to it on the Friday of a difficult week. The third reading is when it begins to land.</p>
          </div>
        </section>

        {/* SECTION 1 — THE 3 REVEALS */}
        <section className="bg-brand-bg border-y border-brand-line print:break-before-page">
          <div className="container-narrow max-w-3xl py-16 md:py-24">
            <p className="overline text-brand-rust">Section one</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-brand-ink leading-tight">
              What pressure reveals
            </h2>
            <p className="mt-6 text-lg text-brand-inkMuted leading-relaxed">
              Pressure does not <em>build</em> leaders. Pressure <em>reveals</em> the leader you already were. There are three things it tends to reveal, almost always in this order.
            </p>

            <div className="mt-12 space-y-12">
              <div>
                <span className="font-display text-6xl text-brand-rust leading-none">01</span>
                <h3 className="font-serif text-2xl text-brand-ink mt-4 leading-snug">Whether you&apos;ve trained, or you&apos;ve talked.</h3>
                <p className="mt-4 text-brand-ink leading-relaxed text-lg">
                  Under pressure, leaders fall to the level of their preparation — not their best day, not their pep talk, not their values statement. The level they have actually <em>practiced</em>. We drill in the dark, with our gloves off, with the radio crackling, with someone yelling, because that is the operating environment of every consequential leadership moment. The hard conversation. The bet-the-company call. The first board meeting after the miss. If you have not rehearsed those, pressure will find that out within ninety seconds.
                </p>
              </div>

              <div>
                <span className="font-display text-6xl text-brand-rust leading-none">02</span>
                <h3 className="font-serif text-2xl text-brand-ink mt-4 leading-snug">Whether you protect the standard or the relationship.</h3>
                <p className="mt-4 text-brand-ink leading-relaxed text-lg">
                  The leader who needs to be liked will quietly soften the standard the moment the room gets cold. The leader who can be trusted holds the standard <em>and</em> the relationship at the same time, even when it costs them socially. That is the test, and most of us fail it the first three or four times. The good ones notice the failure and stop failing it.
                </p>
              </div>

              <div>
                <span className="font-display text-6xl text-brand-rust leading-none">03</span>
                <h3 className="font-serif text-2xl text-brand-ink mt-4 leading-snug">Whether you take the weight or transfer it down.</h3>
                <p className="mt-4 text-brand-ink leading-relaxed text-lg">
                  Under real pressure, weak leaders get loud. Good leaders get clear. Great leaders get quiet, take responsibility for the choice in front of them, and shield their people from the noise long enough for the team to do the work. Watch what a leader does with the weight when it lands. It tells you everything.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 — THE 3 DRILLS */}
        <section className="container-narrow max-w-3xl py-16 md:py-24 print:break-before-page">
          <p className="overline text-brand-rust">Section two</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 text-brand-ink leading-tight">
            How to train for it
          </h2>
          <p className="mt-6 text-lg text-brand-inkMuted leading-relaxed">
            You do not pass the pressure test by hoping the test never comes. You pass it by training in three places, weekly, before you need it. These are the three drills I would put on the wall above your desk.
          </p>

          <div className="mt-12 space-y-10">
            <div className="border-l-4 border-brand-rust pl-6">
              <p className="overline text-brand-inkMuted">Drill one · Friday, 15 minutes</p>
              <h3 className="font-serif text-2xl text-brand-ink mt-2">The written review.</h3>
              <p className="mt-3 text-brand-ink leading-relaxed text-lg">
                Every Friday, fifteen minutes, on paper. Three questions:<br />
                <em>What did I avoid this week?</em><br />
                <em>What standard did I let drift?</em><br />
                <em>What conversation did I postpone?</em>
              </p>
              <p className="mt-3 text-brand-ink leading-relaxed text-lg">
                Naming it is the start of stopping it. Almost everyone is too busy on Friday afternoon for this drill. That is exactly why it works — the leaders who do it consistently are the leaders who, six months later, are operating at a different altitude than their peers.
              </p>
            </div>

            <div className="border-l-4 border-brand-rust pl-6">
              <p className="overline text-brand-inkMuted">Drill two · Once a week, every week</p>
              <h3 className="font-serif text-2xl text-brand-ink mt-2">One uncomfortable conversation.</h3>
              <p className="mt-3 text-brand-ink leading-relaxed text-lg">
                Not the easy conversation. Not the productive conversation. The conversation you have been carrying for two weeks and are quietly hoping resolves itself. The first ten will feel awful. The next forty will feel like leadership. By month three you will have rebuilt the trust on your team without anyone realising what changed — except your team, who will feel safer in your presence than they have in years.
              </p>
            </div>

            <div className="border-l-4 border-brand-rust pl-6">
              <p className="overline text-brand-inkMuted">Drill three · Once a week, before noon</p>
              <h3 className="font-serif text-2xl text-brand-ink mt-2">One decision made faster than feels comfortable.</h3>
              <p className="mt-3 text-brand-ink leading-relaxed text-lg">
                Decision speed is a muscle that atrophies in safe rooms. Pick one real decision a week — a hire, a budget shift, a project to kill — and make it in less time than you think it deserves. You will be wrong sometimes. Wrong-and-fast beats slow-and-frozen, and your team needs the signal that the room can move.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3 — THE 5 QUESTIONS */}
        <section className="bg-brand-bg border-y border-brand-line print:break-before-page">
          <div className="container-narrow max-w-3xl py-16 md:py-24">
            <p className="overline text-brand-rust">Section three</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-brand-ink leading-tight">
              Five questions to ask yourself before the next hard call.
            </h2>
            <p className="mt-6 text-lg text-brand-inkMuted leading-relaxed">
              Print this. Tape it inside the cover of your notebook. Read it before the next conversation you are dreading.
            </p>

            <ol className="mt-12 space-y-8">
              {[
                {
                  slug: "best-leader",
                  q: "What is the version of this call my best leader would make?",
                  a: <>Not my safest. Not my smartest. The version the person I&apos;m trying to become would make.</>,
                },
                {
                  slug: "standard",
                  q: "What is the standard I am about to protect — or surrender?",
                  a: <>Name it in one sentence. If you can&apos;t, you don&apos;t actually have one yet.</>,
                },
                {
                  slug: "cost",
                  q: "Who pays the cost of me getting this wrong?",
                  a: <>Not in dollars. In trust, in time, in the next eighteen months of culture.</>,
                },
                {
                  slug: "kind-direct",
                  q: "What is the kindest, most direct sentence I can lead with?",
                  a: <>Most leaders default to either kind <em>or</em> direct. The reps are in being both at once.</>,
                },
                {
                  slug: "nothing-changed",
                  q: "If I knew nothing changed, would I still feel this is the right call?",
                  a: <>Pressure makes us optimize for what we expect to happen. This question puts you back in front of the call itself.</>,
                },
              ].map(({ slug, q, a }, i) => (
                <li key={slug} className="flex gap-5">
                  <span className="font-display text-5xl text-brand-rust leading-none flex-shrink-0 w-12">{(i+1).toString().padStart(2,"0")}</span>
                  <div>
                    <p className="font-serif text-xl text-brand-ink leading-snug">{q}</p>
                    <p className="mt-2 text-brand-inkMuted leading-relaxed text-base">{a}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* SECTION 4 — 30-DAY STARTER */}
        <section className="container-narrow max-w-3xl py-16 md:py-24 print:break-before-page">
          <p className="overline text-brand-rust">Section four</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 text-brand-ink leading-tight">
            A thirty-day starter plan.
          </h2>
          <p className="mt-6 text-lg text-brand-inkMuted leading-relaxed">
            If you take only one thing from this manual, take this. It will not turn you into a different leader in thirty days. It will turn you into a leader your team experiences differently, by the end of week three — which is where the compounding starts.
          </p>

          <div className="mt-12 space-y-8">
            <div>
              <p className="overline text-brand-inkMuted">Week one</p>
              <h3 className="font-serif text-xl text-brand-ink mt-2">Define the three standards.</h3>
              <p className="mt-3 text-brand-ink leading-relaxed text-lg">
                On a single sheet of paper, write the three non-negotiable standards you hold for your team. Not five. Three. Read them aloud. If even one feels untrue, rewrite it. Carry the sheet in your back pocket for the rest of the month.
              </p>
            </div>
            <div>
              <p className="overline text-brand-inkMuted">Week two</p>
              <h3 className="font-serif text-xl text-brand-ink mt-2">Have the conversation you&apos;ve been carrying.</h3>
              <p className="mt-3 text-brand-ink leading-relaxed text-lg">
                One person. Calendar it for Tuesday. Twenty minutes. Open with the kindest, most direct sentence you can write. Close with what you need from them next.
              </p>
            </div>
            <div>
              <p className="overline text-brand-inkMuted">Week three</p>
              <h3 className="font-serif text-xl text-brand-ink mt-2">Make a fast decision in public.</h3>
              <p className="mt-3 text-brand-ink leading-relaxed text-lg">
                In your next leadership meeting, take one item that would normally be tabled and decide it in the room. Explain the trade-off in two sentences. Move on. Watch how the energy in the room changes for the next thirty minutes.
              </p>
            </div>
            <div>
              <p className="overline text-brand-inkMuted">Week four</p>
              <h3 className="font-serif text-xl text-brand-ink mt-2">Write the Friday review you wish someone had asked you to write twenty years ago.</h3>
              <p className="mt-3 text-brand-ink leading-relaxed text-lg">
                Forty-five minutes. Honest. What did you avoid? What standard drifted? What conversation are you still postponing? Then write the first line of next month&apos;s plan. The first line is the only line that matters.
              </p>
            </div>
          </div>
        </section>

        {/* CLOSING + CTA */}
        <section className="bg-brand-ink text-white print:hidden">
          <div className="container-narrow max-w-3xl py-20 text-center">
            <p className="overline text-brand-rust">One last thing</p>
            <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight">
              If this resonated, the course goes <em className="italic text-brand-rust">much deeper.</em>
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed text-lg max-w-2xl mx-auto">
              <strong>Forged in Fire: The Course</strong> is the full version of what this manual sketches. Twelve modules, forty-eight lessons, the same drills expanded into a deliberate practice that compounds over ninety days.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link
                to="/p/prd_b8dca9fc6cf1"
                className="bg-white text-brand-ink hover:bg-brand-bg px-7 py-4 text-sm font-medium tracking-wide transition-colors inline-flex items-center gap-2"
                data-testid="field-manual-course-cta"
              >
                See the full course <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/preview"
                className="border border-white/40 text-white hover:bg-white hover:text-brand-ink px-7 py-4 text-sm font-medium tracking-wide transition-colors"
              >
                Try Module 1 free →
              </Link>
            </div>
            <p className="mt-10 overline text-white/40">— Robbie South</p>
          </div>
        </section>
      </article>

      <style>{`
        .field-manual .prose-essay p {
          color: #0F172A;
          font-size: 1.125rem;
          line-height: 1.75;
          margin-top: 1.25rem;
        }
        @media print {
          .field-manual { font-size: 11pt; }
          .field-manual section { padding: 1.5rem 0 !important; }
          .field-manual .page-cover { min-height: 100vh; }
          nav, footer, [data-testid="navbar"] { display: none !important; }
        }
      `}</style>
    </Layout>
  );
}
